import React, { useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import { Button, ButtonIcon, DateRangePicker, Input } from '..';
import { GLOBAL_ICONS } from '../../util/vars';
import { DATEPICKER_RANGE } from '../../util/data-id';
import { formatDateForDisplay } from '../../util/dates';
import { validateNoOverlaps } from './validateNoOverlaps';
import type { EffectiveTermDate, PutSchoolTermDateOverrideRequestBody } from '@whizz/api-model';

type Row = {
  termLabel: string;
  termName: string;
  termGroup: string;
  termGroupName: string;
  startDate: string;
  endDate: string;
  source: 'territory' | 'school';
  isNew?: boolean;
  isRemoved?: boolean;
};

interface Props {
  terms: EffectiveTermDate[];
  readOnly?: boolean;
  onSave: (overrides: PutSchoolTermDateOverrideRequestBody['overrides']) => Promise<void> | void;
  onResetToDefault?: (termLabel: string) => Promise<void> | void;
}

const toISODate = (date: Date): string => date.toISOString().slice(0, 10);

const TermDatesEditor = ({ terms, readOnly = false, onSave, onResetToDefault }: Props): JSX.Element => {
  const { translate: t } = useI18n();
  const [ rows, setRows ] = useState<Row[]>(terms.map((term) => ({ ...term })));
  const [ error, setError ] = useState<string | null>(null);
  const [ isSaving, setIsSaving ] = useState(false);
  const [ editingNameFor, setEditingNameFor ] = useState<string | null>(null);
  const [ editingGroupNameFor, setEditingGroupNameFor ] = useState<string | null>(null);
  const [ open, setOpen ] = useState<Record<string, boolean>>({});
  const [ activeDateEdit, setActiveDateEdit ] = useState<{ termLabel: string; field: 'start' | 'end' } | null>(null);

  const visibleRows = rows.filter((row) => !row.isRemoved);

  const groupedTerms = (() => {
    const byGroup = new Map<string, Row[]>();

    for (const row of visibleRows) {
      const group = byGroup.get(row.termGroup) ?? [];
      group.push(row);
      byGroup.set(row.termGroup, group);
    }

    return Array.from(byGroup.entries())
      .map(([ termGroup, groupRows ]) => {
        const sorted = [ ...groupRows ].sort((a, b) => a.startDate.localeCompare(b.startDate));
        return {
          termGroup,
          termGroupName: sorted[0].termGroupName,
          rows: sorted,
          startDate: sorted[0].startDate,
          endDate: sorted[sorted.length - 1].endDate,
        };
      })
      .sort((a, b) => a.startDate.localeCompare(b.startDate));
  })();

  const updateRow = (termLabel: string, patch: Partial<Row>) => {
    setRows((prev) => prev.map((row) => (row.termLabel === termLabel ? { ...row, ...patch } : row)));
  };

  const renameGroup = (termGroup: string, termGroupName: string) => {
    setRows((prev) => prev.map((row) => (row.termGroup === termGroup ? { ...row, termGroupName } : row)));
  };

  const removeRow = (termLabel: string) => {
    setRows((prev) => prev
      .map((row) => (row.termLabel === termLabel ? { ...row, isRemoved: true } : row))
      .filter((row) => !(row.isNew && row.termLabel === termLabel)),
    );
  };

  const removeGroup = (termGroup: string) => {
    setRows((prev) => prev
      .map((row) => (row.termGroup === termGroup ? { ...row, isRemoved: true } : row))
      .filter((row) => !(row.isNew && row.termGroup === termGroup)),
    );
  };

  const addHalfTermTo = (group: (typeof groupedTerms)[number]) => {
    const newLabel = `custom_${Date.now()}`;
    const startDate = new Date(group.endDate);
    startDate.setDate(startDate.getDate() + 1);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 7);

    setRows((prev) => [
      ...prev,
      {
        termLabel: newLabel,
        termName: t('pages.ADMIN.IMPACT_REPORTS.TERM_DATES.NEW_HALF_TERM'),
        termGroup: group.termGroup,
        termGroupName: group.termGroupName,
        startDate: toISODate(startDate),
        endDate: toISODate(endDate),
        source: 'school',
        isNew: true,
      },
    ]);
  };

  const addTerm = () => {
    const newGroup = `group_${Date.now()}`;
    const newLabel = `custom_${Date.now()}`;
    const startDate = toISODate(new Date());
    const endDate = toISODate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));

    setRows((prev) => [
      ...prev,
      {
        termLabel: newLabel,
        termName: t('pages.ADMIN.IMPACT_REPORTS.TERM_DATES.NEW_HALF_TERM'),
        termGroup: newGroup,
        termGroupName: t('pages.ADMIN.IMPACT_REPORTS.TERM_DATES.NEW_TERM'),
        startDate,
        endDate,
        source: 'school',
        isNew: true,
      },
    ]);
    setEditingGroupNameFor(newGroup);
  };

  const toggleOpen = (termGroup: string) => {
    setOpen((prev) => ({ ...prev, [termGroup]: !prev[termGroup] }));
  };

  const handleSave = async () => {
    setError(null);

    const overlapCheck = visibleRows.map((row) => ({ termLabel: row.termLabel, startDate: row.startDate, endDate: row.endDate }));
    const overlap = validateNoOverlaps(overlapCheck);

    if (overlap) {
      setError(t('pages.ADMIN.IMPACT_REPORTS.TERM_DATES.OVERLAP_ERROR', { term: overlap.termLabel, other: overlap.overlapsWithTermLabel }));
      return;
    }

    const overrides: PutSchoolTermDateOverrideRequestBody['overrides'] = rows
      .filter((row) => row.isRemoved || row.isNew)
      .map((row) => (row.isRemoved
        ? { termLabel: row.termLabel, startDate: null, endDate: null }
        : {
          termLabel: row.termLabel,
          termName: row.termName,
          termGroup: row.termGroup,
          termGroupName: row.termGroupName,
          startDate: row.startDate,
          endDate: row.endDate,
        }));

    // Any existing row whose dates/name/term changed also needs to be included as an override.
    for (const row of visibleRows) {
      if (row.isNew) continue;

      const original = terms.find((term) => term.termLabel === row.termLabel);
      if (original && (
        original.startDate !== row.startDate
        || original.endDate !== row.endDate
        || original.termName !== row.termName
        || original.termGroupName !== row.termGroupName
      )) {
        overrides.push({
          termLabel: row.termLabel,
          termName: row.termName,
          termGroup: row.termGroup,
          termGroupName: row.termGroupName,
          startDate: row.startDate,
          endDate: row.endDate,
        });
      }
    }

    setIsSaving(true);
    try {
      await onSave(overrides);
    } catch {
      setError(t('pages.IMPLEMENTATION_ACTION_STEPS.SAVE_ERROR'));
    } finally {
      setIsSaving(false);
    }
  };

  const gridColumns = 'grid-cols-7';

  const halfTermCount = (count: number) => (1 === count
    ? t('pages.ADMIN.IMPACT_REPORTS.TERM_DATES.HALF_TERM_COUNT_ONE', { count })
    : t('pages.ADMIN.IMPACT_REPORTS.TERM_DATES.HALF_TERM_COUNT_OTHER', { count }));

  return (
    <div className={'space-y-3'}>
      {error && <p className={'text-negative text-sm'}>{error}</p>}
      <div className={'border border-gray-200 rounded-lg overflow-hidden'}>
        <div className={`grid ${gridColumns} gap-2 bg-gray-100 px-3 py-2 text-xs font-medium text-gray-500`}>
          <span className={'col-span-2'}>{t('pages.ADMIN.IMPACT_REPORTS.TERM_DATES.TERM_HALF_TERM')}</span>
          <span className={'col-span-2'}>{t('pages.ADMIN.IMPACT_REPORTS.TERM_DATES.STARTS')}</span>
          <span className={'col-span-2'}>{t('pages.ADMIN.IMPACT_REPORTS.TERM_DATES.ENDS')}</span>
          <span />
        </div>
        {groupedTerms.map((group) => {
          const isOpen = open[group.termGroup];

          return (
            <div key={group.termGroup}>
              <div
                className={`grid ${gridColumns} gap-2 items-center px-3 py-2 border-t border-gray-100 bg-gray-50 cursor-pointer`}
                onClick={() => toggleOpen(group.termGroup)}
              >
                <div className={'flex items-center gap-2 min-w-0 col-span-2'}>
                  <span className={'text-primary text-xs w-3 flex-none'}>{isOpen ? '▾' : '▸'}</span>
                  {editingGroupNameFor === group.termGroup ? (
                    <Input
                      id={`termGroupName-${group.termGroup}`}
                      label={t('pages.ADMIN.IMPACT_REPORTS.TERM_DATES.EDIT_TERM_NAME')}
                      hideLabel
                      autoFocus
                      value={group.termGroupName}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => renameGroup(group.termGroup, e.target.value)}
                      onBlur={() => setEditingGroupNameFor(null)}
                      onKeyDown={(e) => {
                        if ('Enter' === e.key) {
                          e.preventDefault();
                          setEditingGroupNameFor(null);
                        }
                      }}
                    />
                  ) : (
                    <>
                      <span className={'font-semibold truncate'}>{group.termGroupName}</span>
                      <span className={'text-gray-400 text-xs whitespace-nowrap'}>{halfTermCount(group.rows.length)}</span>
                      {!readOnly && (
                        <ButtonIcon
                          type={'button'}
                          icon={'Edit'}
                          size={'sm'}
                          asLink
                          title={t('pages.ADMIN.IMPACT_REPORTS.TERM_DATES.EDIT_TERM_NAME')}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingGroupNameFor(group.termGroup);
                          }}
                        />
                      )}
                    </>
                  )}
                </div>
                <span className={'font-medium text-gray-700 col-span-2'}>{formatDateForDisplay(group.startDate)}</span>
                <span className={'font-medium text-gray-700 col-span-2'}>{formatDateForDisplay(group.endDate)}</span>
                <div className={'flex items-center justify-end'}>
                  {!readOnly && (
                    <ButtonIcon
                      type={'button'}
                      icon={GLOBAL_ICONS.delete}
                      size={'sm'}
                      asLink
                      title={t('pages.ADMIN.IMPACT_REPORTS.TERM_DATES.DELETE_TERM')}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeGroup(group.termGroup);
                      }}
                    />
                  )}
                </div>
              </div>

              {isOpen && group.rows.map((row) => {
                const selectsStart = activeDateEdit?.termLabel === row.termLabel && 'start' === activeDateEdit?.field;
                const selectsEnd = activeDateEdit?.termLabel === row.termLabel && 'end' === activeDateEdit?.field;
                const isEditingDates = selectsStart || selectsEnd;

                const dateButtonClass = (active: boolean) =>
                  `truncate text-left rounded border px-2 py-1 w-full ${active ? 'border-primary font-bold text-primary' : 'border-gray-200 text-gray-700'}`;

                const toggleDateEdit = (field: 'start' | 'end') => {
                  if (readOnly) return;
                  setActiveDateEdit((prev) => (prev?.termLabel === row.termLabel && prev?.field === field ? null : { termLabel: row.termLabel, field }));
                };

                return (
                  <React.Fragment key={row.termLabel}>
                    <div className={`grid ${gridColumns} gap-2 items-center px-3 py-2 border-t border-gray-100`}>
                      {editingNameFor === row.termLabel ? (
                        <div className={'col-span-2'}>
                          <Input
                            id={`termName-${row.termLabel}`}
                            label={t('pages.ADMIN.IMPACT_REPORTS.TERM_DATES.EDIT_PERIOD_NAME')}
                            hideLabel
                            autoFocus
                            value={row.termName}
                            onChange={(e) => updateRow(row.termLabel, { termName: e.target.value })}
                            onBlur={() => setEditingNameFor(null)}
                            onKeyDown={(e) => {
                              if ('Enter' === e.key) {
                                e.preventDefault();
                                setEditingNameFor(null);
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <div className={'flex items-center gap-1 min-w-0 pl-5 col-span-2'}>
                          <span className={'w-2 h-px bg-gray-300 flex-none'} />
                          <span className={'truncate'}>{row.termName}</span>
                          {!readOnly && (
                            <ButtonIcon
                              type={'button'}
                              icon={'Edit'}
                              size={'sm'}
                              asLink
                              title={t('pages.ADMIN.IMPACT_REPORTS.TERM_DATES.EDIT_PERIOD_NAME')}
                              onClick={() => setEditingNameFor(row.termLabel)}
                            />
                          )}
                        </div>
                      )}
                      <button
                        type={'button'}
                        disabled={readOnly}
                        className={`${dateButtonClass(selectsStart)} col-span-2`}
                        onClick={() => toggleDateEdit('start')}
                      >
                        {formatDateForDisplay(row.startDate)}
                      </button>
                      <button
                        type={'button'}
                        disabled={readOnly}
                        className={`${dateButtonClass(selectsEnd)} col-span-2`}
                        onClick={() => toggleDateEdit('end')}
                      >
                        {formatDateForDisplay(row.endDate)}
                      </button>
                      <div className={'flex items-center gap-2 justify-end'}>
                        {!readOnly && 'school' === row.source && onResetToDefault && !row.isNew && (
                          <ButtonIcon
                            type={'button'}
                            icon={'Refresh'}
                            size={'sm'}
                            asLink
                            title={t('pages.COUNTRY_TERM_DATES.RESET')}
                            onClick={async () => {
                              try {
                                await onResetToDefault(row.termLabel);
                              } catch {
                                setError(t('pages.IMPLEMENTATION_ACTION_STEPS.SAVE_ERROR'));
                              }
                            }}
                          />
                        )}
                        {!readOnly && (
                          <ButtonIcon
                            type={'button'}
                            icon={GLOBAL_ICONS.delete}
                            size={'sm'}
                            asLink
                            onClick={() => removeRow(row.termLabel)}
                          />
                        )}
                      </div>
                    </div>
                    {isEditingDates && (
                      <div className={'px-3 pb-3 border-t-0 flex justify-center'}>
                        <DateRangePicker
                          calendarClassName={DATEPICKER_RANGE}
                          showMonthDropdown
                          showYearDropdown
                          hideInputs
                          selected={selectsStart ? new Date(row.startDate) : new Date(row.endDate)}
                          selectsStart={selectsStart}
                          selectsEnd={selectsEnd}
                          startDate={new Date(row.startDate)}
                          endDate={new Date(row.endDate)}
                          minDate={selectsEnd ? new Date(row.startDate) : undefined}
                          maxDate={undefined}
                          onChange={(date: Date) => {
                            updateRow(row.termLabel, selectsStart ? { startDate: toISODate(date) } : { endDate: toISODate(date) });
                            setActiveDateEdit(null);
                          }}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}

              {isOpen && !readOnly && (
                <div className={'pl-5 pb-2 pt-1 border-t-0'}>
                  <button
                    type={'button'}
                    className={'text-sm font-medium text-primary'}
                    onClick={() => addHalfTermTo(group)}
                  >
                    {t('pages.ADMIN.IMPACT_REPORTS.TERM_DATES.ADD_HALF_TERM_TO', { term: group.termGroupName })}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {!readOnly && (
        <div className={'flex items-center gap-4'}>
          <Button className={'flex-none'} type={'button'} isOutline inlineText onClick={addTerm}>
            {t('pages.ADMIN.IMPACT_REPORTS.TERM_DATES.ADD_TERM')}
          </Button>
          <span className={'flex-1 min-w-0 text-xs text-gray-400'}>{t('pages.ADMIN.IMPACT_REPORTS.TERM_DATES.ADD_TERM_HINT')}</span>
          <Button className={'flex-none'} type={'button'} inlineText onClick={handleSave} disabled={isSaving}>
            {t('pages.ADMIN.IMPACT_REPORTS.TERM_DATES.SAVE')}
          </Button>
        </div>
      )}
    </div>
  );
};

export { TermDatesEditor };
